import * as React from 'react';
import { Popover as BasePopover } from '@base-ui/react/popover';
import { cn } from '../../lib/utils';

/**
 * Popover — shadcn (base-vega) on Base UI.
 *
 * Base UI splits the old single Radix `Content` into `Portal` + `Positioner`
 * + `Popup`. We preserve the shadcn API by wrapping all three inside our
 * `PopoverContent`. `align` and `sideOffset` map onto Positioner.
 *
 * Radix `Anchor` had no direct Base UI equivalent; we re-export `Trigger` as
 * `PopoverAnchor` so call sites keep compiling. If true anchoring is required,
 * use Base UI's `Popover.Trigger render={...}` pattern instead.
 */
const Popover = BasePopover.Root;
const PopoverTrigger = BasePopover.Trigger;
const PopoverAnchor = BasePopover.Trigger;

type PositionerProps = React.ComponentPropsWithoutRef<typeof BasePopover.Positioner>;
type PopupProps = React.ComponentPropsWithoutRef<typeof BasePopover.Popup>;

export interface PopoverContentProps extends PopupProps {
  align?: PositionerProps['align'];
  side?: PositionerProps['side'];
  sideOffset?: PositionerProps['sideOffset'];
  alignOffset?: PositionerProps['alignOffset'];
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className, align = 'center', side, sideOffset = 4, alignOffset, ...props }, ref) => (
    <BasePopover.Portal>
      <BasePopover.Positioner
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
      >
        <BasePopover.Popup
          ref={ref}
          className={cn(
            'z-50 w-72 rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-md outline-none',
            'data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[open]:zoom-in-95',
            className,
          )}
          {...props}
        />
      </BasePopover.Positioner>
    </BasePopover.Portal>
  ),
);
PopoverContent.displayName = 'PopoverContent';

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
